import type { EntityDefinitions, EntityPropertyDefinitions } from '@/types/eds/Definitions';
import type { EDSV2ArrayResponse } from '@/types/eds/api/responses/EDSV2Response';
import type { NormalizedV2Property, NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import type { DecisionTableDefinition } from '@/domains/decisionTables/types/DecisionTableDefinition';
import { call, put, takeLatest } from 'redux-saga/effects';
import { EdsDataTypes } from '@/features/eds/enums/EdsDataTypes';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import {
  fetchDecisionTables,
  fetchDecisionTablesFailed,
  fetchDecisionTablesSuccess,
} from '@/domains/decisionTables/store/decisionTablesSlice';
import HttpService from '@/services/HttpService/HttpService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';

type DecisionTableListResponse = {
  listings: EDSV2ArrayResponse;
};

type DecisionTableRelationship = {
  entityType?: string;
  EntityType?: string;
  relationshipLabel?: string | null;
  RelationshipLabel?: string | null;
  [key: string]: unknown;
};

const normalizeRelationshipDefinition = (
  relationshipDefinition: EntityPropertyDefinitions['relationshipDefinition'] | DecisionTableRelationship | undefined,
): DecisionTableRelationship | undefined => {
  if (Array.isArray(relationshipDefinition)) {
    return relationshipDefinition[0];
  }

  return relationshipDefinition;
};

const getRelationshipEntityType = (relationship?: DecisionTableRelationship) => {
  return relationship?.entityType || relationship?.EntityType;
};

const getDecisionTableLabel = (
  fieldName: string,
  field: EntityPropertyDefinitions | NormalizedV2Property,
  entityType: string,
) => {
  const relationship = normalizeRelationshipDefinition(field.relationshipDefinition);

  return (
    field.displayTitle ||
    relationship?.relationshipLabel ||
    relationship?.RelationshipLabel ||
    field.fieldName ||
    fieldName ||
    entityType
  );
};

const buildDecisionTableDefinitions = (decisionTables: NormalizedV2Response[]): DecisionTableDefinition[] => {
  return decisionTables.flatMap((decisionTable) =>
    Object.entries(decisionTable.fields).reduce<DecisionTableDefinition[]>((definitions, [fieldName, field]) => {
      const relationship = normalizeRelationshipDefinition(field?.relationshipDefinition);
      const entityType = getRelationshipEntityType(relationship);

      if (!field || field.dataType !== EdsDataTypes.DataTable || !entityType) {
        return definitions;
      }

      return [
        ...definitions,
        {
          id: `${decisionTable.id}-${fieldName}-${entityType}`,
          label: getDecisionTableLabel(fieldName, field, entityType),
          entityType,
          relationship,
          parentEntityType: decisionTable.entityName,
          parentEntityId: decisionTable.id,
          sourceFieldName: fieldName,
          sourceField: field,
        },
      ];
    }, []),
  );
};

function* fetchDecisionTableEntitiesSaga() {
  const { data }: { data: DecisionTableListResponse } = yield call(
    HttpService.post,
    `/filter/${EdsEntityCodes.DecisionTable}?${QueryParamsService.constructSearchParamsFromRecord({
      page: 1,
      perPage: 100,
    })}`,
    [],
    {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    },
  );

  const decisionTables: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings, {
    includeId: true,
    forceUpdate: false,
  });

  return decisionTables;
}

const buildDecisionTableDefinitionsFallback = (
  entityDefinition: EntityDefinitions | null,
): DecisionTableDefinition[] => {
  if (!entityDefinition?.fields) return [];

  return Object.entries(entityDefinition.fields).reduce<DecisionTableDefinition[]>(
    (definitions, [fieldName, field]) => {
      const relationship = normalizeRelationshipDefinition(field?.relationshipDefinition);
      const entityType = getRelationshipEntityType(relationship);

      if (!field || field.dataType !== EdsDataTypes.DataTable || !entityType) {
        return definitions;
      }

      return [
        ...definitions,
        {
          id: `${fieldName}-${entityType}`,
          label: getDecisionTableLabel(fieldName, field, entityType),
          entityType,
          relationship,
          parentEntityType: EdsEntityCodes.DecisionTable,
          parentEntityId: 0,
          sourceFieldName: fieldName,
          sourceField: field,
        },
      ];
    },
    [],
  );
};

function* fetchDecisionTablesSaga() {
  try {
    const entityDefinition: EntityDefinitions | null = yield call(
      EdsAdapterService.getEntityDefinition,
      EdsEntityCodes.DecisionTable,
      true,
    );

    const decisionTables: NormalizedV2Response[] = yield call(fetchDecisionTableEntitiesSaga);
    const definitions = buildDecisionTableDefinitions(decisionTables);

    yield put(
      fetchDecisionTablesSuccess(
        definitions.length ? definitions : buildDecisionTableDefinitionsFallback(entityDefinition),
      ),
    );
  } catch (error) {
    console.error('Failed to fetch decision table definitions:', error);
    yield put(fetchDecisionTablesFailed('Failed to load decision tables. Please try again.'));
  }
}

export function* decisionTablesWatcher() {
  yield takeLatest(fetchDecisionTables.type, fetchDecisionTablesSaga);
}
